import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    FaUser,
    FaGraduationCap,
    FaBriefcase,
    FaCamera,
    FaSave,
    FaFileUpload,
    FaCheckCircle,
    FaGithub,
    FaLinkedin
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './JobSeekerProfile.css';

const JobSeekerProfile = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        name: '',
        phone: '',
        bio: '',
        location: '',
        profileImage: '',
        academicInfo: {
            education: '',
            skills: [],
            github: '',
            linkedin: ''
        },
        careerPreferences: {
            preferredJobTypes: [],
            preferredLocations: [],
            expectedSalary: ''
        },
        resumeUrl: ''
    });

    const [inputStates, setInputStates] = useState({
        skills: '',
        preferredJobTypes: '',
        preferredLocations: ''
    });

    const fileInputRef = useRef(null);
    const resumeInputRef = useRef(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                const data = res.data;
                setProfile({
                    name: data.name || '',
                    phone: data.phone || '',
                    bio: data.bio || '',
                    location: data.location || '',
                    profileImage: data.profileImage || '',
                    academicInfo: {
                        education: data.academicInfo?.education || '',
                        skills: data.academicInfo?.skills || [],
                        github: data.academicInfo?.github || '',
                        linkedin: data.academicInfo?.linkedin || ''
                    },
                    careerPreferences: {
                        preferredJobTypes: data.careerPreferences?.preferredJobTypes || [],
                        preferredLocations: data.careerPreferences?.preferredLocations || [],
                        expectedSalary: data.careerPreferences?.expectedSalary || ''
                    },
                    resumeUrl: data.resumeUrl || ''
                });

                setInputStates({
                    skills: (data.academicInfo?.skills || []).join(', '),
                    preferredJobTypes: (data.careerPreferences?.preferredJobTypes || []).join(', '),
                    preferredLocations: (data.careerPreferences?.preferredLocations || []).join(', ')
                });
                setLoading(false);
            } catch (err) {
                console.error("Error fetching job seeker profile", err);
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setProfile(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setProfile(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleArrayInputChange = (field, value) => {
        setInputStates(prev => ({ ...prev, [field]: value }));

        // Sync with profile state (actual array)
        const items = value.split(',').map(s => s.trim()).filter(s => s !== '');
        if (field === 'skills') {
            setProfile(prev => ({
                ...prev,
                academicInfo: { ...prev.academicInfo, skills: items }
            }));
        } else {
            setProfile(prev => ({
                ...prev,
                careerPreferences: { ...prev.careerPreferences, [field]: items }
            }));
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        showLoading('Uploading Image...');
        try {
            const res = await axios.post('/api/users/profile-image', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, profileImage: res.data.profileImage }));
            closeLoading();
            showSuccess('Success', 'Profile image updated.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            closeLoading();
            showError('Failed', 'Could not upload image.');
        }
    };

    const handleResumeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('resume', file);
        showLoading('Uploading Resume...');
        try {
            const res = await axios.post('/api/users/resume-upload', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, resumeUrl: res.data.resumeUrl }));
            closeLoading();
            showSuccess('Success', 'Resume stored in secure vault.');
        } catch (err) {
            closeLoading();
            showError('Failed', 'Resume upload failed.');
        }
    };

    const handleViewResume = () => {
        if (profile.resumeUrl) {
            window.open(`${API_URL}${profile.resumeUrl}`, '_blank');
        } else {
            showError('No Resume', 'You have not uploaded a resume yet.');
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Synchronizing Professional Data...');
        try {
            await axios.put('/api/users/profile', profile, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Synced', 'Your career profile is now unified across all dashboards.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            closeLoading();
            showError('Sync Error', 'Failed to synchronize professional data.');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="js-jobseeker-profile js-fade-in">
            <div className="js-dashboard-header">
                <h1 className="js-page-title">Career Profile Dashboard</h1>
                <p className="js-page-subtitle">Refine your professional credentials and target preferences for real-time recruiter matching.</p>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="js-profile-sections-grid">
                    {/* Left Panel */}
                    <div className="js-profile-left-panel">
                        <div className="js-profile-card js-text-center">
                            <div className="js-avatar-upload">
                                <div className="js-avatar-large">
                                    {profile.profileImage ? (
                                        <img src={`${API_URL}${profile.profileImage}`} alt="Profile" />
                                    ) : (
                                        profile.name?.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <button type="button" className="js-cam-btn" onClick={() => fileInputRef.current.click()}>
                                    <FaCamera />
                                </button>
                                <input type="file" ref={fileInputRef} hidden onChange={handleImageUpload} accept="image/*" />
                            </div>
                            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>{profile.name}</h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '20px', fontWeight: 600 }}>Professional Job Seeker</p>

                            <div style={{ textAlign: 'left', marginTop: '20px' }}>
                                <div className="js-sync-status">
                                    <FaCheckCircle /> Profile Ready for Evaluation
                                </div>
                            </div>
                        </div>

                        <div className="js-profile-card" style={{ marginTop: '24px' }}>
                            <h3 className="js-section-title-sm"><FaFileUpload /> Professional Assets</h3>
                            <div className="js-resume-strip">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '24px', color: '#0284C7' }}><FaFileUpload /></span>
                                    <div>
                                        <p style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Resume / CV</p>
                                        <p style={{ fontSize: '11px', color: '#64748B', margin: 0 }}>
                                            {profile.resumeUrl ? 'Verified Document' : 'No document uploaded'}
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {profile.resumeUrl && (
                                        <button
                                            type="button"
                                            className="js-primary-btn js-sm"
                                            onClick={handleViewResume}
                                            style={{ background: '#002C5F', width: 'auto', padding: '0 12px' }}
                                            title="View Resume"
                                        >
                                            View
                                        </button>
                                    )}
                                    <button type="button" className="js-primary-btn js-sm" onClick={() => resumeInputRef.current.click()} style={{ background: '#0284C7' }}>
                                        {profile.resumeUrl ? 'Replace' : 'Upload'}
                                    </button>
                                </div>
                                <input type="file" ref={resumeInputRef} hidden onChange={handleResumeUpload} accept=".pdf,.doc,.docx" />
                            </div>
                        </div>
                    </div>

                    {/* Right Panel */}
                    <div className="js-profile-right-panel">
                        <div className="js-section-card">
                            <h3 className="js-section-title"><FaUser /> Personal Identification</h3>
                            <div className="js-form-row">
                                <div className="js-form-group">
                                    <label>Full Name</label>
                                    <input className="js-glass-input" name="name" value={profile.name} onChange={handleChange} placeholder="e.g. Abhishek Kumar" />
                                </div>
                                <div className="js-form-group">
                                    <label>Primary Contact (Phone)</label>
                                    <input className="js-glass-input" name="phone" value={profile.phone} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
                                </div>
                            </div>
                            <div className="js-form-group">
                                <label>Base Location</label>
                                <input className="js-glass-input" name="location" value={profile.location} onChange={handleChange} placeholder="e.g. Bangalore, India" />
                            </div>
                            <div className="js-form-group">
                                <label>Executive Summary (Bio)</label>
                                <textarea className="js-glass-input js-resize-none" name="bio" value={profile.bio} onChange={handleChange} rows="3" placeholder="Succinctly describe your core strengths and career objectives..."></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="js-section-card js-full-width-card">
                        <h3 className="js-section-title"><FaGraduationCap /> Professional Background</h3>
                        <div className="js-form-group">
                            <label>Educational Foundation</label>
                            <input className="js-glass-input" name="academicInfo.education" value={profile.academicInfo.education} onChange={handleChange} placeholder="e.g. B.Tech Computer Science, XYZ University (2024)" />
                        </div>
                        <div className="js-form-group">
                            <label>Technical Proficiencies (Comma separated)</label>
                            <input
                                className="js-glass-input"
                                value={inputStates.skills}
                                onChange={(e) => handleArrayInputChange('skills', e.target.value)}
                                placeholder="React, Node.js, AWS, TensorFlow..."
                            />
                        </div>
                        <div className="js-form-row">
                            <div className="js-form-group">
                                <label><FaGithub /> Engineering Repository (GitHub)</label>
                                <input className="js-glass-input" name="academicInfo.github" value={profile.academicInfo.github} onChange={handleChange} placeholder="https://github.com/your-id" />
                            </div>
                            <div className="js-form-group">
                                <label><FaLinkedin /> Professional Network (LinkedIn)</label>
                                <input className="js-glass-input" name="academicInfo.linkedin" value={profile.academicInfo.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/your-profile" />
                            </div>
                        </div>
                    </div>

                    <div className="js-section-card js-full-width-card">
                        <h3 className="js-section-title"><FaBriefcase /> Matching Preferences</h3>
                        <div className="js-form-row">
                            <div className="js-form-group">
                                <label>Target Professional Roles (Comma separated)</label>
                                <input
                                    className="js-glass-input"
                                    value={inputStates.preferredJobTypes}
                                    onChange={(e) => handleArrayInputChange('preferredJobTypes', e.target.value)}
                                    placeholder="Full Stack Developer, DevOps Engineer..."
                                />
                            </div>
                            <div className="js-form-group">
                                <label>Compensation Expectation (Annual)</label>
                                <input className="js-glass-input" name="careerPreferences.expectedSalary" value={profile.careerPreferences.expectedSalary} onChange={handleChange} placeholder="e.g. $80,000 per annum" />
                            </div>
                        </div>
                        <div className="js-form-group">
                            <label>Preferred Operating Hubs (comma separated)</label>
                            <input
                                className="js-glass-input"
                                value={inputStates.preferredLocations}
                                onChange={(e) => handleArrayInputChange('preferredLocations', e.target.value)}
                                placeholder="Remote, Hyderabad, Gurgaon..."
                            />
                        </div>
                    </div>

                    <div className="js-full-width-card" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '32px' }}>
                        <button type="submit" className="js-primary-btn js-pulse-on-hover" style={{ padding: '12px 36px' }}>
                            <FaSave /> Synchronize Professional Profile
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default JobSeekerProfile;
